import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { config } from '../config';

interface GoogleAccountsId {
  initialize: (cfg: {
    client_id: string;
    callback: (response: { credential: string }) => void;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      theme?: string;
      size?: string;
      text?: string;
      shape?: string;
      width?: number;
    }
  ) => void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface LocationState {
  from?: { pathname: string };
}

export default function Login() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [scriptError, setScriptError] = useState(false);

  const next =
    new URLSearchParams(location.search).get('next') ??
    (location.state as LocationState | null)?.from?.pathname ??
    '/';

  useEffect(() => {
    if (user) {
      navigate(next, { replace: true });
    }
  }, [user, navigate, next]);

  useEffect(() => {
    if (user) return;

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GSI_SCRIPT_SRC}"]`
    );
    let script: HTMLScriptElement | null = null;

    const init = () => {
      if (!window.google?.accounts?.id || !buttonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: config.oauthClientId,
        callback: (response) => {
          try {
            signIn(response.credential);
            navigate(next, { replace: true });
          } catch (err) {
            setError(
              err instanceof Error ? err.message : 'Could not validate Google sign-in.'
            );
          }
        },
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width: 300,
      });
    };

    if (existing && window.google?.accounts?.id) {
      init();
    } else {
      script = existing ?? document.createElement('script');
      if (!existing) {
        script.src = GSI_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = init;
        script.onerror = () => setScriptError(true);
        document.body.appendChild(script);
      } else {
        existing.addEventListener('load', init);
      }
    }

    return () => {
      if (script && !existing) {
        script.onload = null;
        script.onerror = null;
      }
    };
  }, [user, signIn, navigate, next]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm space-y-8 text-center">
        <div className="space-y-3">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">InfiOps</h1>
          <p className="text-sm text-gray-600">Ingestion Operations Center</p>
        </div>

        {error && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-left text-sm text-red-700">
            {error}
          </div>
        )}

        {scriptError && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-left text-sm text-amber-800">
            Could not load Google Sign-In. Check your network or browser
            extensions, then refresh.
          </div>
        )}

        <div className="flex justify-center">
          <div ref={buttonRef} />
        </div>

        <p className="text-xs text-gray-500">
          Sign in with your <span className="font-medium">@infidimes.com</span>{' '}
          Google account.
        </p>
      </div>
    </div>
  );
}
